import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, TextField } from '@mui/material';
import { ModalBaseProps } from 'types';
import { bulkSetUsersTemporaryPassword } from 'thunk';
import SimpleModal from 'components/SimpleModal';
import Copy from 'components/Copy';
import { selectTeamMembersHash, selectAuthToken } from 'selectors';
import { PromiseAllSettledStatus } from 'services/constants';
import { generateStrongPassword } from 'utils';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { useNotification } from 'hooks/useNotification';
import * as ProfileServices from 'ProfileModule/services';
import ApiClientManager from 'core/apiClient';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';

type ModalProps = ModalBaseProps & {
  accountIds: number[];
  teamId: number;
  /** Tenant name for API context - ensures actions target the correct tenant */
  tenant?: string;
  /** Realm ID for API context - ensures actions target the correct realm */
  realmId?: string;
};

const passwordPolicies = [
  {
    label: 'At least 14 characters',
  },
  {
    label: 'At least one number',
  },
  {
    label: 'At least one lower case letter',
  },
  {
    label: 'At least one upper case letter',
  },
  {
    label: 'At least one special character',
  },
];

const SetUsersTemporaryPasswordModal = ({
  open,
  onClose,
  accountIds,
  teamId,
  tenant,
  realmId,
}: ModalProps) => {
  const dispatch = useDispatch();
  const [tempPassword, setTempPassword] = useState('');
  const memberHash = useSelector(selectTeamMembersHash);
  const token = useSelector(selectAuthToken);
  const [isRequesting, setIsRequesting] = useState(false);
  const { getSandboxCredentialsWarningMessage } = useSandboxes();
  const { showError, showWarning, showSuccess } = useNotification();
  const warningMessage = getSandboxCredentialsWarningMessage({
    teamId,
  });

  // Only has a value when setting new password is successful
  const [passwordToCopy, setPasswordToCopy] = useState('');

  const handleResponse = (response: PromiseSettledResult<any>[], newPassword: string) => {
    let successfulCount = 0;
    let failureCount = 0;
    let successMsg = 'Successfully set password for:';
    let failureMsg = 'Failed to set password for:';
    response.forEach((result, index) => {
      let userName: string;

      if (result.status === PromiseAllSettledStatus.Rejected) {
        const rejectedResult = result as PromiseRejectedResult & {
          reason?: {
            config: { data: any };
            data: unknown;
          };
        };
        try {
          const data = JSON.parse(rejectedResult.reason?.config?.data || '{}');
          userName =
            memberHash?.[data?.['account_id']]?.name ||
            String(data?.['account_id'] || `User ${index + 1}`);
          const errorMessages = extractApiErrorMessage(rejectedResult.reason);
          failureMsg += `\n ${userName}: ${errorMessages}`;
        } catch {
          // JSON parsing failed - add fallback error message
          failureMsg += `\n User ${index + 1}: Unknown error`;
        }
        failureCount++;
      } else {
        const fulfilledResult = result as PromiseFulfilledResult<any>;
        const accountId = fulfilledResult.value?.data?.['account_id'];
        userName = memberHash?.[accountId]?.name || String(accountId);
        successMsg += `\n ${userName}`;
        successfulCount++;
      }
    });

    // Set password to copy once if any succeeded (same password for all operations)
    if (successfulCount >= 1) {
      setPasswordToCopy(newPassword);
    }

    // Show appropriate notification
    if (successfulCount >= 1 && failureCount >= 1) {
      showWarning(`${successMsg}\n${failureMsg}`, 6000);
    } else if (successfulCount >= 1) {
      showSuccess(successMsg);
    } else if (failureCount >= 1) {
      showError(failureMsg);
    }
    setIsRequesting(false);
  };

  const onSetPassword = async () => {
    const newPassword = tempPassword.trim();

    if (!newPassword) {
      // ✅ FIX: Replace alert() with toast notification
      showError('Password cannot be empty');
      return;
    }

    const accountIdAndPasswordPair = accountIds.map((id) => {
      return { account_id: id, password: newPassword };
    });
    setIsRequesting(true);

    // Use explicit tenant/realm context if provided
    if (tenant && realmId && token) {
      const headers = ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId });
      const promises = accountIdAndPasswordPair.map((item) =>
        ProfileServices.setSingleUserTemporaryPassword(item, { headers }, token),
      );
      const response = await Promise.allSettled(promises);
      handleResponse(response, newPassword);
    } else {
      // Fall back to global context via thunk
      // TODO: Fix with Redux Toolkit configureStore migration
      dispatch(
        bulkSetUsersTemporaryPassword({
          accountIdAndPasswordPair,
          onSuccess: ({ response }) => {
            handleResponse(response, newPassword);
          },
        }) as any,
      );
    }
  };

  const handleClose = () => {
    if (isRequesting) {
      return;
    }
    onClose();
  };

  const handlePasswordOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTempPassword(value);
  };

  const handleGenerateRandomTempPassword = () => {
    const newTempPassword = generateStrongPassword();
    setTempPassword(newTempPassword);
  };

  return (
    <SimpleModal
      open={open}
      onClose={handleClose}
      onConfirm={onSetPassword}
      headerText={
        <div>
          Setting New Password for{' '}
          <u>
            {accountIds?.length === 1
              ? memberHash?.[accountIds[0]]?.name || '1 member'
              : `${accountIds.length} members`}
          </u>
          {warningMessage ? (
            <Box
              sx={{
                display: 'block',
                color: 'red',
              }}
            >
              {warningMessage}
            </Box>
          ) : (
            <></>
          )}
        </div>
      }
      Body={
        <>
          <Box component={'p'}>
            <b>Enter the new password or use the generate password button</b>
          </Box>
          <TextField
            fullWidth
            label="New Password"
            value={tempPassword}
            placeholder="Example: Password12345!"
            onChange={handlePasswordOnChange}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
          <Box component={'ul'}>
            {passwordPolicies.map((policy) => (
              <li key={policy.label}>{policy.label}</li>
            ))}
          </Box>
          <Box
            sx={{
              marginTop: 2,
            }}
          >
            <Button variant="contained" color="primary" onClick={handleGenerateRandomTempPassword}>
              Generate Random Password
            </Button>
          </Box>
          {passwordToCopy && (
            <Box
              sx={{
                marginTop: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                New Password:{' '}
                <b>
                  <u>{passwordToCopy}</u>
                </b>
                <Box
                  sx={{
                    marginLeft: 3,
                  }}
                >
                  <Copy textToCopy={passwordToCopy} />
                </Box>
              </Box>
            </Box>
          )}
        </>
      }
      cancelButtonText={passwordToCopy ? 'close' : 'cancel'}
      disableConfirmButton={isRequesting}
      disableCancelButton={isRequesting}
    />
  );
};

export default SetUsersTemporaryPasswordModal;
